import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import Link from 'next/link'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { studioMeta } from '@/constants/site'
import { socials, legalLinks } from '@/constants/socials'
import { NomaMark } from '@/components/generals/noma-mark'
import { Wordmark } from '@/components/generals/wordmark'
import { RollText } from '@/components/generals/roll-link'

/* The footer keeps its own list rather than repeating the header menu, so it
   can point straight at the two ways in: the scan and the call. */
const footerLinks = [
    { label: 'Services', href: '/#services' },
    { label: 'Projects', href: '/projects', count: '(3)' },
    { label: 'Free Market Scan', href: '/#scan-form' },
    { label: 'Book a call', href: '/#book-a-call' },
    { label: 'Contact', href: '/contact' }
]

const ArrowIcon: FC = (): ReactNode => (
    <svg viewBox='0 0 24 24' className='size-3.5 transition-transform duration-500 group-hover/roll:rotate-45' fill='none' aria-hidden='true'>
        <path d='M5 19L19 5M19 5H8M19 5v11' stroke='currentColor' strokeWidth='2' />
    </svg>
)

export const SiteFooter: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion()) {
            return
        }

        /* The footer rises out from under the last section rather than simply
           scrolling into view, which is what gives the page its closing beat. */
        gsap.fromTo('[data-footer-lift]',
            { yPercent: -12 },
            {
                yPercent: 0,
                ease: 'none',
                scrollTrigger: {
                    trigger: root,
                    start: 'top bottom',
                    end: 'top 40%',
                    scrub: 0.55
                }
            }
        )

        gsap.fromTo('[data-footer-item]',
            { opacity: 0, y: 34 },
            {
                opacity: 1,
                y: 0,
                duration: 1,
                stagger: 0.06,
                ease: 'noma',
                scrollTrigger: {
                    trigger: root,
                    start: 'top 78%',
                    once: true
                }
            }
        )
    }, scope, [])

    return (
        <footer
            ref={scope}
            id='footer'
            className='relative z-10 overflow-hidden bg-[#f7f7f7] py-10 text-ink lg:py-12'
        >
            <div
                data-footer-lift
                className='mx-auto flex max-w-[1920px] flex-col gap-9 px-5 lg:gap-12 lg:px-[33px]'
            >
                {/* Top: mark + footer links */}
                <div className='grid grid-cols-2 gap-5 lg:grid-cols-4'>
                    <Link
                        href='/'
                        data-footer-item
                        data-cursor='link'
                        aria-label='Strength Scaling, home'
                        className='group/roll flex h-fit w-fit items-center gap-2 text-ink'
                    >
                        <NomaMark className='size-[24px] transition-colors duration-500 group-hover/roll:text-brand' />

                        <RollText label='Strength Scaling' className='t-display pb-[0.06em] text-[19px] leading-none' />
                    </Link>

                    <nav
                        className='col-span-1 flex flex-wrap items-start justify-end gap-x-8 gap-y-3 lg:col-span-3'
                        aria-label='Footer'
                    >
                        {footerLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                data-footer-item
                                data-cursor='link'
                                className='group/roll flex items-start gap-1 text-ink transition-colors duration-300 hover:text-brand'
                            >
                                <RollText label={link.label} className='t-cond pb-[0.08em] text-[17px] leading-none lg:text-[23px]' />

                                {link.count
                                    ? (
                                        <span className='t-mono mt-[2px] text-[10px] text-brand'>
                                            {link.count}
                                        </span>
                                    )
                                    : null}
                            </Link>
                        ))}
                    </nav>
                </div>

                {/* Middle: copyright, wordmark, statement and the two ways in */}
                <div className='hair-t grid gap-8 pt-9 lg:grid-cols-4'>
                    <div className='flex flex-col gap-8'>
                        <span data-footer-item className='t-cond-soft text-[12px] leading-[1.3] text-ash'>
                            <span className='block'>
                                © 2026 Strength Scaling LLC.
                            </span>

                            <span className='block'>
                                All rights reserved.
                            </span>
                        </span>

                        <div data-footer-item className='flex flex-col gap-2'>
                            <span className='t-mono text-ash'>
                                legal
                            </span>

                            {legalLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    data-cursor='link'
                                    className='group/roll t-mono w-fit text-ink transition-colors duration-300 hover:text-brand'
                                >
                                    <RollText label={link.label} />
                                </Link>
                            ))}
                        </div>

                        <div data-footer-item className='flex flex-col gap-2'>
                            <span className='t-mono text-ash'>
                                contact
                            </span>

                            <a
                                href={`mailto:${studioMeta.email}`}
                                data-cursor='link'
                                className='group/roll t-mono w-fit normal-case text-ink transition-colors duration-300 hover:text-brand'
                            >
                                <RollText label={studioMeta.email} />
                            </a>

                            <a
                                href={`tel:${studioMeta.phoneTel}`}
                                data-cursor='link'
                                className='group/roll t-mono nums w-fit text-ink transition-colors duration-300 hover:text-brand'
                            >
                                <RollText label={studioMeta.phone} />
                            </a>
                        </div>
                    </div>

                    <div className='flex flex-col gap-8 lg:col-span-3'>
                        <Wordmark />

                        <div className='flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between'>
                            <p
                                data-footer-item
                                className='t-cond max-w-[52ch] text-[17px] leading-[1.15] text-ash lg:text-[23px]'
                            >
                                <span className='text-ink'>
                                    The growth system for local businesses.
                                </span>
                                {' '}
                                Websites that get found, follow-up that never misses a lead, and ads when you&apos;re ready.
                            </p>

                            <div data-footer-item className='flex shrink-0 flex-col gap-5'>
                                <div className='flex flex-wrap gap-3'>
                                    <Link
                                        href='/#scan-form'
                                        data-cursor='link'
                                        className='group/roll t-cond inline-flex items-center gap-3 rounded-[var(--radius-field)] bg-[#0066FF] px-5 py-3.5 text-[15px] text-white transition-colors duration-300 hover:bg-[#0052cc] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0066FF]'
                                    >
                                        <RollText label='Get your free Market Scan' className='pb-[0.08em] leading-none' />

                                        <ArrowIcon />
                                    </Link>

                                    <Link
                                        href='/#book-a-call'
                                        data-cursor='link'
                                        className='group/roll t-cond inline-flex items-center gap-3 rounded-[var(--radius-field)] border border-ink px-5 py-3.5 text-[15px] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink'
                                    >
                                        <RollText label='Book a call' className='pb-[0.08em] leading-none' />

                                        <ArrowIcon />
                                    </Link>
                                </div>

                                <div className='flex h-fit items-center gap-4'>
                                    {socials.map((social) => (
                                        <a
                                            key={social.label}
                                            href={social.href}
                                            target='_blank'
                                            rel='noreferrer'
                                            data-cursor='link'
                                            aria-label={`Strength Scaling on ${social.label}`}
                                            className='text-ink transition-colors duration-300 hover:text-brand'
                                        >
                                            <svg viewBox='0 0 24 24' className='size-[18px]' aria-hidden='true'>
                                                <path d={social.path} fill='currentColor' />
                                            </svg>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom line */}
                <div className='flex flex-col gap-8'>
                    <p data-footer-item className='t-mono text-ash'>
                        Serving the Raleigh and Charlotte areas · Built in North Carolina
                    </p>
                </div>
            </div>
        </footer>
    )
}
